import {Close} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {toast} from 'sonner';
import {
  useBulkDeactivationActions,
  useBulkDeactivationStatus,
} from './useBulkDeactivation';

type DeactivationProgressToastProps = {
  toastId: string | number;
};

const getDeactivationTitle = (isComplete: boolean, succeeded: number) => {
  if (!isComplete) return 'Deactivating members';

  const memberLabel = succeeded === 1 ? 'member' : 'members';
  return `Deactivated ${succeeded} ${memberLabel}`;
};

const getDeactivationSubtitle = (
  isComplete: boolean,
  done: number,
  failed: number,
  total: number,
) => {
  if (!isComplete) return `${done} of ${total}`;
  if (failed > 0) return `${failed} could not be deactivated`;
  return 'All done';
};

export const DeactivationProgressToast = ({
  toastId,
}: DeactivationProgressToastProps) => {
  const theme = useTheme();
  const {dismiss} = useBulkDeactivationActions();
  const {counts, run} = useBulkDeactivationStatus();

  if (!run) return null;

  const {total, done, succeeded, failed, isComplete} = counts;
  const progress = total === 0 ? 0 : (done / total) * 100;
  const title = getDeactivationTitle(isComplete, succeeded);
  const subtitle = getDeactivationSubtitle(isComplete, done, failed, total);

  const handleClose = () => {
    toast.dismiss(toastId);
    dismiss();
  };

  return (
    <Box
      sx={{
        width: 320,
        padding: 1.5,
        borderRadius: theme.tokens.radius.md,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[3],
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
        {!isComplete && <CircularProgress size={18} thickness={5} />}
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{fontSize: 12, color: theme.palette.text.secondary}}>
            {subtitle}
          </Typography>
        </Box>
        {isComplete && (
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Dismiss"
            data-attr="admin-members-deactivation-dismiss"
          >
            <Close sx={{fontSize: 16}} />
          </IconButton>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        color={isComplete && failed > 0 ? 'warning' : 'primary'}
        sx={{mt: 1.25, height: 6, borderRadius: 3}}
      />
    </Box>
  );
};
